import { useCallback, useMemo, useState } from 'react';
import {
  Campaign,
  Status_Enum as CampaignStatus,
  CampaignType_Enum as CampaignType,
  ListRecipientsRequest_EngagementFilter as EngagementFilter,
  ListRecipientsRequest_StatusFilter as StatusFilter,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import {
  useUpdateCampaign,
  useDeleteCampaign,
  useExpandCampaignsSummary,
  useListRecipients,
  getCampaignsSort,
} from '@frontend/api-bulk-messaging';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Chip,
  Info,
  SpinningLoader,
  Table,
  TableColumnConfig,
  TableRowActions,
  Text,
  useAlert,
  useModalControl,
} from '@frontend/design-system';
import { useBulkNavigator } from '../../../hooks/use-bulk-navigator';
import { CampaignGroup, ContainerSize } from '../../../types';
import { createCampaignStatusUpdateRequest } from '../../../utils/create-campaign-status-update-request';
import { isReadOnly } from '../../../utils/is-read-only';
import { BulkMessageSegmentedBar } from '../../bulk-message-segmented-bar';
import { BulkMessagingModal } from '../../modals/bulk-messaging-modal';
import { DeleteCampaignModal } from '../../modals/delete-campaign-modal';
import { RecipientModal } from '../../modals/recipient-modal';
import { CellValue } from './cell-value';
import { LabelledIcon } from './labelled-icon';

type Props = {
  campaignGroup: CampaignGroup;
  campaignType: CampaignType;
  graphSize: ContainerSize;
  hasAllotment: boolean;
  selectedLocationIds: string[];
  trackingIdPrefix: string;
  year: number;
};

export const CampaignGroupTable = ({
  campaignGroup,
  campaignType,
  graphSize,
  hasAllotment,
  selectedLocationIds,
  trackingIdPrefix,
  year,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const navigateTo = useBulkNavigator(campaignType);
  const alert = useAlert();
  const [campaignToCancel, setCampaignToCancel] = useState<Campaign>();
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign>();
  const { modalProps: cancelModalProps, openModal: openCancelModal } = useModalControl();
  const { modalProps: deleteModalProps, openModal: openDeleteModal } = useModalControl();
  const { modalProps: recipientsModalProps, openModal: openRecipientsModal } = useModalControl();
  const { selectedOrgId: orgId, getScopeName } = useAppScopeStore();

  const { isLoading, expandMonth } = useExpandCampaignsSummary((err) => {
    alert.error(t('An error occurred while loading your campaigns. Please try again.'));
    console.error('An error occurred while expanding a month.', err);
  });

  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [selectedEngagementFilter, setSelectedEngagementFilter] = useState<EngagementFilter>(
    EngagementFilter.ALL_ENGAGEMENT_TYPES
  );
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<StatusFilter>(StatusFilter.ALL_STATUSES);

  const usage = campaignGroup.usage;
  const direction = getCampaignsSort();

  const modalTitle = useMemo(
    () => (selectedEngagementFilter === EngagementFilter.NEGATIVE ? t('Unsubscribers') : t('Recipients')),
    [selectedEngagementFilter, t]
  );

  const recipients = useListRecipients({
    orgId,
    campaignId: selectedCampaignId,
    statusFilter: selectedStatusFilter,
    engagementFilter: selectedEngagementFilter,
    pageSize: 25,
    pageToken: '',
  });

  const { mutateAsync: updateCampaign } = useUpdateCampaign(campaignType);
  const { mutate: deleteCampaign } = useDeleteCampaign();

  const showCancelDialog = useCallback(
    (campaign: Campaign) => {
      setCampaignToCancel(campaign);
      openCancelModal();
    },
    [openCancelModal]
  );

  const closeCancelDialog = useCallback(() => {
    setCampaignToCancel(undefined);
    cancelModalProps.onClose();
  }, [cancelModalProps]);

  const handleCancelCampaign = useCallback(() => {
    if (!campaignToCancel || !campaignToCancel.campaignId) return;
    const cancelledCampaignRequest = createCampaignStatusUpdateRequest(
      orgId,
      CampaignStatus.CANCELED,
      campaignToCancel
    );
    updateCampaign(cancelledCampaignRequest);
    setCampaignToCancel(undefined);
    cancelModalProps.onClose();
  }, [campaignToCancel, cancelModalProps, orgId, updateCampaign]);

  const showDeleteDialog = useCallback(
    (campaign: Campaign) => {
      setCampaignToDelete(campaign);
      openDeleteModal();
    },
    [openDeleteModal]
  );

  const closeDeleteDialog = useCallback(() => {
    setCampaignToDelete(undefined);
    deleteModalProps.onClose();
  }, [deleteModalProps]);

  const handleDeleteCampaign = useCallback(() => {
    if (!campaignToDelete || !campaignToDelete.campaignId) return;
    deleteCampaign({ orgId, campaignId: campaignToDelete.campaignId });
    setCampaignToDelete(undefined);
    deleteModalProps.onClose();
  }, [campaignToDelete, deleteModalProps, deleteCampaign, orgId]);

  const triggerRecipientModal = useCallback(
    (
      campaignId = '',
      engagementFilter = EngagementFilter.ALL_ENGAGEMENT_TYPES,
      statusFilter = StatusFilter.ALL_STATUSES
    ) => {
      setSelectedCampaignId(campaignId);
      setSelectedEngagementFilter(engagementFilter);
      setSelectedStatusFilter(statusFilter);
      openRecipientsModal();
    },
    [openRecipientsModal]
  );

  const onExpand = useCallback(() => {
    const month = campaignGroup.month;
    if (!month) return;
    expandMonth(
      {
        orgId,
        locationIds: selectedLocationIds,
        year,
        sort: direction,
        types: [campaignType],
      },
      month
    );
  }, [campaignGroup.month, campaignType, direction, expandMonth, orgId, selectedLocationIds, year]);

  const hasSelectedLocations = selectedLocationIds.length > 1;

  const colConfig: TableColumnConfig<Campaign>[] = useMemo(
    () => [
      {
        Header: t('Date'),
        id: 'startSendAt',
        width: 70,
        disableSortBy: true,
        accessor: ({ startSendAt }) => formatDate(startSendAt, 'Do'),
      },
      {
        Header: t('Name'),
        id: 'title',
        width: 200,
        disableSortBy: true,
        accessor: ({ title }) => title ?? t('Untitled Campaign'),
        cellConfig: {
          element: 'TextLink',
          onClick: (_, { campaignId }) => navigateTo.editor({ campaignId }),
          trackingId: `${trackingIdPrefix}-table-name-link`,
        },
      },
      {
        Header: t('Status'),
        id: 'status',
        width: 80,
        disableSortBy: true,
        accessor: ({ currentStatus }) => currentStatus,
        cellRenderer: (status: CampaignStatus) => {
          switch (status) {
            case CampaignStatus.SCHEDULED:
              return (
                <LabelledIcon label={t('Scheduled')}>
                  <Icon name='calendar-confirmed' color='light' />
                </LabelledIcon>
              );
            case CampaignStatus.PROCESSING:
              return (
                <LabelledIcon label={t('Processing')}>
                  <Icon name='pending' color='light' />
                </LabelledIcon>
              );
            case CampaignStatus.COMPLETED:
              return (
                <LabelledIcon label={t('Completed')}>
                  <Icon name='check' color='light' />
                </LabelledIcon>
              );
            case CampaignStatus.FAILED:
              return (
                <LabelledIcon label={t('Failed')}>
                  <Icon name='alert' color='light' />
                </LabelledIcon>
              );
            case CampaignStatus.CANCELED:
              return (
                <LabelledIcon label={t('Canceled')}>
                  <Icon name='block' color='light' />
                </LabelledIcon>
              );
            case CampaignStatus.DRAFT: // Drafts should never be in this table
              return (
                <LabelledIcon label={t('Draft')}>
                  <Icon name='alert' color='error' />
                </LabelledIcon>
              );
            case CampaignStatus.HIDDEN_DRAFT: // Hidden drafts should never be in this table
              return (
                <LabelledIcon label={t('Hidden Draft')}>
                  <Icon name='alert' color='error' />
                </LabelledIcon>
              );
            case CampaignStatus.DELETED: // Deleted campaigns should never be in this table
              return (
                <LabelledIcon label={t('Deleted')}>
                  <Icon name='alert' color='error' />
                </LabelledIcon>
              );
            default: // Unspecified campaigns should never be in this table
              return (
                <LabelledIcon label={t('Unspecified')}>
                  <Icon name='alert' color='error' />
                </LabelledIcon>
              );
          }
        },
      },
      {
        Header: t('Total Messages'),
        id: 'messages',
        disableSortBy: true,
        width: 120,
        omit: campaignType === CampaignType.EMAIL,
        accessor: (campaign) => (
          <CellValue value={campaign.usage?.total ?? 0} trackingId={`${trackingIdPrefix}-table-messages-btn`} />
        ),
      },
      {
        Header: t('Recipients'),
        id: 'recipients',
        disableSortBy: true,
        width: 110,
        accessor: ({ campaignId, messageCounts }) => (
          <CellValue
            value={messageCounts?.total ?? 0}
            onClick={() => triggerRecipientModal(campaignId)}
            trackingId={`${trackingIdPrefix}-table-recipients-btn`}
          />
        ),
      },
      {
        Header: t('Delivered To'),
        id: 'delivered',
        disableSortBy: true,
        width: 120,
        accessor: ({ campaignId, currentStatus, messageCounts }) => (
          <CellValue
            value={messageCounts?.delivered ?? 0}
            status={currentStatus}
            onClick={() =>
              triggerRecipientModal(campaignId, EngagementFilter.ALL_ENGAGEMENT_TYPES, StatusFilter.DELIVERED)
            }
            trackingId={`${trackingIdPrefix}-table-delivered-to-btn`}
          />
        ),
      },
      {
        Header: t('Failed'),
        id: 'failed',
        disableSortBy: true,
        width: 80,
        accessor: ({ campaignId, currentStatus, messageCounts }) => (
          <CellValue
            value={messageCounts?.failed ?? 0}
            status={currentStatus}
            onClick={() =>
              triggerRecipientModal(campaignId, EngagementFilter.ALL_ENGAGEMENT_TYPES, StatusFilter.FAILED)
            }
            trackingId={`${trackingIdPrefix}-table-failed-btn`}
          />
        ),
      },
      {
        Header: t('Open Rate'),
        id: 'openRate',
        disableSortBy: true,
        width: 110,
        omit: campaignType === CampaignType.SMS,
        accessor: ({ currentStatus, openRate }) => (
          <CellValue isDecimal isPercentage status={currentStatus} value={openRate} />
        ),
      },
      {
        Header: (
          <span
            css={{
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {t('Engagement')}
            <Info
              css={{
                color: theme.colors.neutral30,
                marginLeft: theme.spacing(1),
              }}
              data-trackingid={`${trackingIdPrefix}-table-engagement-tooltip`}
            >
              {t('Link Clicks / Unsubscribes')}
            </Info>
          </span>
        ),
        id: 'engaged',
        disableSortBy: true,
        width: 140,
        accessor: ({ campaignId, currentStatus, engagements }) => (
          <Text
            css={{
              display: 'flex',
              alignItems: 'baseline',
              margin: 0,
              fontSize: theme.fontSize(14),
            }}
          >
            <CellValue
              value={engagements?.positive ?? 0}
              status={currentStatus}
              onClick={() => triggerRecipientModal(campaignId, EngagementFilter.POSITIVE)}
              trackingId={`${trackingIdPrefix}-table-engaged-btn`}
            />
            <span
              css={{
                margin: theme.spacing(0, 0.25),
              }}
            >
              /
            </span>
            <CellValue
              value={engagements?.negative ?? 0}
              status={currentStatus}
              onClick={() => triggerRecipientModal(campaignId, EngagementFilter.NEGATIVE)}
              trackingId={`${trackingIdPrefix}-table-unsubscribed-btn`}
            />
          </Text>
        ),
      },
      {
        Header: t('Locations'),
        id: 'locations',
        disableSortBy: true,
        width: 150,
        omit: !hasSelectedLocations,
        accessor: ({ locationIds }) => {
          if (!locationIds || locationIds.length === 0) return null;
          return locationIds.length > 1 ? (
            <Chip.MultiChip isResponsive>{t('{{count}} Locations', { count: locationIds.length })}</Chip.MultiChip>
          ) : (
            <Chip.SingleChip isResponsive>{getScopeName(locationIds[0] ?? '')}</Chip.SingleChip>
          );
        },
      },
    ],
    [campaignType, getScopeName, hasSelectedLocations, navigateTo, t, trackingIdPrefix, triggerRecipientModal]
  );

  const rowActions: TableRowActions<Campaign> = useMemo(
    () => ({
      actions: [
        {
          hide: (campaign) => isReadOnly(campaign),
          label: t('View Campaign'),
          Icon: () => <Icon name='preview' />,
          onClick: ({ campaignId }) => navigateTo.editor({ campaignId }),
          trackingId: `${trackingIdPrefix}-table-view-btn`,
        },
        {
          hide: (campaign) => !isReadOnly(campaign),
          label: t('Edit Campaign'),
          Icon: () => <Icon name='edit' />,
          onClick: ({ campaignId }) => navigateTo.editor({ campaignId }),
          trackingId: `${trackingIdPrefix}-table-edit-btn`,
        },
        {
          hide: !hasAllotment,
          label: t('Duplicate Campaign'),
          Icon: () => <Icon name='copy' />,
          onClick: ({ campaignId }) => navigateTo.editor({ seedCampaignId: campaignId }),
          trackingId: `${trackingIdPrefix}-table-duplicate-btn`,
        },
        // { // TODO: Enable this after implementing duplicate with recipients in the duplicate view
        //   hide: !hasAllotment,
        //   label: t('Duplicate with Recipients'),
        //   Icon: () => <Icon name='family' />,
        //   onClick: ({campaignId}) => navigateTo.editor({ seedCampaignId: campaignId, includeRecipients: true }),
        //   trackingId: `${trackingIdPrefix}-table-duplicate-with-recipients-btn`,
        // },
        {
          hide: ({ currentStatus }) =>
            [CampaignStatus.CANCELED, CampaignStatus.COMPLETED, CampaignStatus.DELETED].includes(currentStatus),
          label: t('Cancel Campaign'),
          Icon: () => <Icon name='block' />,
          onClick: (campaign) => showCancelDialog(campaign),
          trackingId: `${trackingIdPrefix}-table-cancel-btn`,
        },
        {
          hide: ({ messageCounts }) => (messageCounts?.delivered ?? 0) > 0,
          label: <Text color='error'>{t('Delete Campaign')}</Text>,
          Icon: () => <Icon color='error' name='trash' />,
          onClick: (campaign) => showDeleteDialog(campaign),
          trackingId: `${trackingIdPrefix}-table-delete-btn`,
        },
      ],
    }),
    [hasAllotment, navigateTo, showCancelDialog, showDeleteDialog, t, trackingIdPrefix]
  );

  return (
    <article
      css={{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(2),
      }}
    >
      <BulkMessageSegmentedBar
        month={campaignGroup.month}
        sent={usage.sent}
        failed={usage.error}
        pending={usage.scheduled}
        quota={usage.allotment}
        used={(usage.sent ?? 0) + (usage.scheduled ?? 0)}
        size={graphSize}
        year={year}
        showAllotments={hasSelectedLocations}
      />

      {campaignGroup.campaigns.length > 0 && (
        <Table<Campaign>
          data={campaignGroup.campaigns}
          collapsedTableConfig={
            campaignGroup.campaigns.length > 4
              ? {
                  rowsToShow: 4,
                  collapseLabel: t('Show Less...'),
                  expandLabel: t('Show More...'),
                  startingValue: false,
                  onExpand,
                }
              : undefined
          }
          colConfig={colConfig}
          rowActions={rowActions}
        />
      )}

      {isLoading && (
        <div
          css={{
            textAlign: 'right',
            marginTop: theme.spacing(-5.5),
            marginRight: theme.spacing(16),
          }}
        >
          <SpinningLoader size='xs' />
        </div>
      )}

      <BulkMessagingModal
        {...cancelModalProps}
        onClose={closeCancelDialog}
        title={t('Cancel Campaign')}
        trackingIdPrefix={`${trackingIdPrefix}-cancel-modal`}
      >
        <Text
          css={{
            textAlign: 'left',
          }}
        >
          {t('Are you sure you want to cancel {{campaignName}}? This action cannot be undone.', {
            campaignName: campaignToCancel?.title || t('this campaign'),
          })}
        </Text>
        <BulkMessagingModal.ButtonBar
          onAction={handleCancelCampaign}
          onCancel={closeCancelDialog}
          actionText={t('Cancel Campaign')}
          cancelText={t('Cancel')}
          trackingId={`${trackingIdPrefix}-cancel-modal`}
        />
      </BulkMessagingModal>

      <DeleteCampaignModal
        {...deleteModalProps}
        campaign={campaignToDelete}
        onCancel={closeDeleteDialog}
        onClose={closeDeleteDialog}
        onDelete={handleDeleteCampaign}
        trackingIdPrefix={trackingIdPrefix}
      />

      <RecipientModal
        {...recipientsModalProps}
        recipients={recipients}
        title={modalTitle}
        trackingIdPrefix={trackingIdPrefix}
      />
    </article>
  );
};
